import { create } from 'zustand'
import { getCookie, setCookie, removeCookie } from '@/lib/cookies'
import { ensureAuthUser } from '@/lib/ensure-auth-user'
import { setSentryUser, clearSentryUser } from '@/lib/sentry'
import { setActivePanelTimezone, PLATFORM_TIMEZONE } from '@/lib/panel-timezone'
import {
  isGlobalOrganizationId,
  resolvePreferredOrganizationId,
} from '@/lib/resolve-active-organization'

const ACCESS_TOKEN = 'shadcn_admin_access_token'
const REFRESH_TOKEN = 'shadcn_admin_refresh_token'
const TAB_ACCESS_TOKEN = 'shadcn_admin_tab_access_token'

interface AuthUser {
  id: string
  user_id?: string
  email: string
  name?: string
  display_name?: string
  role?: string
  roles: string[]
  permissions: string[]
  type: string
  reseller_id?: string
  customer_id?: string
  active_org_id?: string
  readonly?: boolean
  timezone?: string
  whitelabel_enabled?: boolean
  brand_name?: string
  brand_logo_url?: string
  memberships?: { organizationId: string, role: string, organization: { id: string, name: string } }[]
  exp?: number
}

interface AuthState {
  auth: {
    user: AuthUser | null
    setUser: (user: AuthUser | null) => void
    accessToken: string
    refreshToken: string
    setAccessToken: (accessToken: string) => void
    setRefreshToken: (refreshToken: string) => void
    setTokens: (accessToken: string, refreshToken: string) => void
    setTabAccessToken: (accessToken: string) => void
    clearTabAccessToken: () => void
    getEffectiveAccessToken: () => string
    activeOrganizationId: string | null
    setActiveOrganizationId: (id: string | null) => void
    fetchUser: () => Promise<void>
    reset: () => void
  }
}

export const useAuthStore = create<AuthState>()((set, get) => {
  const cookieAccessToken = getCookie(ACCESS_TOKEN)
  const cookieRefreshToken = getCookie(REFRESH_TOKEN)

  const initAccess = cookieAccessToken ? JSON.parse(cookieAccessToken) : ''
  const initRefresh = cookieRefreshToken ? JSON.parse(cookieRefreshToken) : ''

  const initTabAccess =
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem(TAB_ACCESS_TOKEN) ?? ''
      : ''

  return {
    auth: {
      user: null,
      setUser: (user) => {
        // Keep Sentry user context in sync so every event is tagged with who was logged in.
        if (user) {
          setSentryUser({ id: user.id, email: user.email, username: user.display_name ?? user.name, role: user.role })
        } else {
          clearSentryUser()
        }

        const stored =
          typeof window !== 'undefined'
            ? localStorage.getItem('activeOrganizationId')
            : null
        const nextOrgId = resolvePreferredOrganizationId({
          activeOrgId: user?.active_org_id,
          memberships: user?.memberships,
          // Keep a prior real-tenant selection; never keep Global Scope as default.
          storedId: isGlobalOrganizationId(stored) ? null : stored,
        })

        if (nextOrgId) localStorage.setItem('activeOrganizationId', nextOrgId)
        else localStorage.removeItem('activeOrganizationId')

        set((state) => ({
          ...state,
          auth: {
            ...state.auth,
            user,
            activeOrganizationId: nextOrgId,
          },
        }))
      },
      accessToken: initTabAccess || initAccess,
      refreshToken: initRefresh,
      activeOrganizationId: (() => {
        const stored =
          typeof window !== 'undefined'
            ? localStorage.getItem('activeOrganizationId')
            : null
        return isGlobalOrganizationId(stored) ? null : stored
      })(),
      setActiveOrganizationId: (id) =>
        set((state) => {
           if (id) localStorage.setItem('activeOrganizationId', id);
           else localStorage.removeItem('activeOrganizationId');
           return { ...state, auth: { ...state.auth, activeOrganizationId: id } }
        }),
      setAccessToken: (accessToken) =>
        set((state) => {
          if (typeof window !== 'undefined') {
            window.sessionStorage.removeItem(TAB_ACCESS_TOKEN)
          }
          setCookie(ACCESS_TOKEN, JSON.stringify(accessToken))
          return { ...state, auth: { ...state.auth, accessToken } }
        }),
      setRefreshToken: (refreshToken) =>
        set((state) => {
           setCookie(REFRESH_TOKEN, JSON.stringify(refreshToken))
           return { ...state, auth: { ...state.auth, refreshToken } }
        }),
      setTokens: (accessToken, refreshToken) => 
        set((state) => {
            if (typeof window !== 'undefined') {
              window.sessionStorage.removeItem(TAB_ACCESS_TOKEN)
            }
            setCookie(ACCESS_TOKEN, JSON.stringify(accessToken))
            setCookie(REFRESH_TOKEN, JSON.stringify(refreshToken))
            return { ...state, auth: { ...state.auth, accessToken, refreshToken } }
        }),
      setTabAccessToken: (accessToken) =>
        set((state) => {
          if (typeof window !== 'undefined') {
            window.sessionStorage.setItem(TAB_ACCESS_TOKEN, accessToken)
          }
          return { ...state, auth: { ...state.auth, accessToken } }
        }),
      clearTabAccessToken: () =>
        set((state) => {
          if (typeof window !== 'undefined') {
            window.sessionStorage.removeItem(TAB_ACCESS_TOKEN)
          }
          const cookieAccessToken = getCookie(ACCESS_TOKEN)
          const fallbackAccess = cookieAccessToken ? JSON.parse(cookieAccessToken) : ''
          return { ...state, auth: { ...state.auth, accessToken: fallbackAccess } }
        }),
      getEffectiveAccessToken: () => {
        if (typeof window !== 'undefined') {
          const tab = window.sessionStorage.getItem(TAB_ACCESS_TOKEN)
          if (tab) return tab
        }
        const cookieAccessToken = getCookie(ACCESS_TOKEN)
        const cookieAccess = cookieAccessToken ? JSON.parse(cookieAccessToken) : ''
        return cookieAccess || get().auth.accessToken || ''
      },
      fetchUser: () => ensureAuthUser(),
      reset: () => {
        clearSentryUser()
        setActivePanelTimezone(PLATFORM_TIMEZONE)
        set((state) => {
          removeCookie(ACCESS_TOKEN)
          removeCookie(REFRESH_TOKEN)
          if (typeof window !== 'undefined') {
            window.sessionStorage.removeItem(TAB_ACCESS_TOKEN)
          }
          localStorage.removeItem('activeOrganizationId')
          return {
            ...state,
            auth: { ...state.auth, user: null, accessToken: '', refreshToken: '', activeOrganizationId: null },
          }
        })
      },
    },
  }
})
